import { ApolloClient, ApolloLink, createHttpLink, InMemoryCache, ServerError } from '@apollo/client';
// eslint-disable-next-line import/no-extraneous-dependencies
import { onError } from '@apollo/client/link/error';
import { toast } from 'react-toastify';
import '../assets/css/toast.css';
import { NetworkError } from '@apollo/client/errors';

interface RequestBody {
  operationName: string;
}

function compileErrorMsg(networkError: NetworkError): string {
  if (networkError && 'result' in networkError) {
    if (typeof networkError.result === 'string') {
      return networkError.result;
    }
    if (networkError.result.errors) {
      // eslint-disable-next-line
      return networkError.result.errors[0].message;
    }
  }
  return 'Unexpected error';
}

const errorLink = onError(({ networkError }) => {
  // For handling dynamic forms validations errors.
  if (
    networkError &&
    ((networkError as ServerError)?.statusCode === 406 || (networkError as ServerError)?.statusCode === 421)
  ) {
    toast.error(`${compileErrorMsg(networkError)}`, {
      toastId: 'error-toast',
      position: 'top-right',
      autoClose: false,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      className: 'customToast',
    });
    return;
  }

  // For handling authentication errors.
  if ((networkError as ServerError)?.statusCode === 401 || (networkError as ServerError)?.statusCode === 403) {
    window.location.href = `https://${process.env.REACT_APP_AUTH_FE_BASE_URL}/login`;
    return;
  }

  // For handling custom-handled expected errors.
  if (networkError && (networkError as ServerError)?.statusCode === 409) {
    // eslint-disable-next-line no-param-reassign
    networkError.message = compileErrorMsg(networkError);
    return;
  }

  /** @deprecated (https://app.clickup.com/2336662/v/dc/279wp-785/279wp-45614) Use 409 instead. */
  // For handling dynamic validations without toast.
  if (networkError && (networkError as ServerError)?.statusCode === 418) {
    return;
  }

  // For handling dynamic validations without toast.
  // @deprecated, use 409 code
  if (networkError && (networkError as ServerError)?.statusCode === 418) {
    return;
  }

  if (networkError) {
    toast.error(`${networkError as unknown as string} Error message: ${compileErrorMsg(networkError)}`, {
      toastId: 'error-toast',
      position: 'bottom-right',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  }
});

// Follow the naming convention for all the queries created on front-end. The name should go:
// SERVICE_NAME_nameOfQuery . The service should also be listed here, in customFetch function.
const SERVICE_URLS = {
  DOMAIN_SERVICE_V2: `https://${process.env.REACT_APP_API_BASE_URL}/domainV2/Prod/graphql`,
  CART_SERVICE_V2_: `https://${process.env.REACT_APP_API_BASE_URL}/cartV2/Prod/graphql`,
  ENROLLMENT_SERVICE_: `https://${process.env.REACT_APP_API_BASE_URL}/enrollment/Prod/graphql`,
  GEO_SERVICE_: `https://${process.env.REACT_APP_API_BASE_URL}/geo/Prod/graphql`,
  GROUP_SERVICE_: `https://${process.env.REACT_APP_API_BASE_URL}/groupV2/Prod/graphql`,
  USER_SERVICE_: `https://${process.env.REACT_APP_API_BASE_URL}/userV2/Prod/graphql`,
  PRODUCT_SERVICE_V2_: `https://${process.env.REACT_APP_API_BASE_URL}/productV2/Prod/graphql`,
  PROPOSAL_SERVICE_V3_: `https://${process.env.REACT_APP_API_BASE_URL}/proposalV3/Prod/graphql`,
  FORM_SERVICE_: `https://${process.env.REACT_APP_API_BASE_URL}/form/Prod/graphql`,
  POLICY_SERVICE_: `https://${process.env.REACT_APP_API_BASE_URL}/policy/Prod/graphql`,
  CENSUS_SERVICE_: `https://${process.env.REACT_APP_API_BASE_URL}/censusV2/Prod/graphql`,
  ELECTION_SERVICE_: `https://${process.env.REACT_APP_API_BASE_URL}/election/Prod/graphql`,
  UW_SERVICE_: `https://${process.env.REACT_APP_API_BASE_URL}/uwrule/Prod/graphql`,
  CSV_SERVICE_: `https://${process.env.REACT_APP_API_BASE_URL}/csv/Prod/graphql`,
  PDF_SERVICE_: `https://${process.env.REACT_APP_API_BASE_URL}/pdfRead/Prod/graphql`,
  CARRIER_FILE_SERVICE_: `https://${process.env.REACT_APP_API_BASE_URL}/carrierFileV2/Prod/graphql`,
  EMAIL_SERVICE_: `https://${process.env.REACT_APP_API_BASE_URL}/email/Prod/graphql`,
  ERROR_TRACKING_: `https://${process.env.REACT_APP_API_BASE_URL}/errorTrack/Prod/graphql`,
  EXCHANGE_SERVICE_: `https://${process.env.REACT_APP_API_BASE_URL}/exchange/Prod/graphql`,
  EDI_SERVICE_: `https://${process.env.REACT_APP_API_BASE_URL}/edi/Prod/graphql`,
  ANNOUNCEMENT_SERVICE_: `https://${process.env.REACT_APP_API_BASE_URL}/announcement/Prod/graphql`,
  ZYWAVE_SERVICE_: `https://${process.env.REACT_APP_API_BASE_URL}/zywave/Prod/graphql`,
  AAA_SERVICE_: `https://${process.env.REACT_APP_API_BASE_URL}/aaa/Prod/graphql`,
  ANALYTICS_SERVICE_: `https://${process.env.REACT_APP_API_BASE_URL}/analytics/Prod/graphql`,
  DOCUMENTS_SERVICE_: `https://${process.env.REACT_APP_API_BASE_URL}/documents/Prod/graphql`,
  SIGN_SERVICE_: `https://${process.env.REACT_APP_API_BASE_URL}/sign/Prod/graphql`,
  BI_REPORT_SERVICE_: `https://${process.env.REACT_APP_API_BASE_URL}/biReport/Prod/graphql`,
  IMQS_SERVICE_: `https://${process.env.REACT_APP_API_BASE_URL}/imq/Prod/graphql`,
  ID_TRACKER_SERVICE_: `https://${process.env.REACT_APP_API_BASE_URL}/tracker/Prod/graphql`,
};

const customFetch = (_: unknown, options: RequestInit | undefined): Promise<Response> => {
  if (!options || !options.body) {
    throw new Error('Request is not valid!');
  }
  const body = JSON.parse(options.body as string) as RequestBody;
  const serviceKey = Object.keys(SERVICE_URLS).find((serviceName) => body.operationName.includes(serviceName));
  if (serviceKey) {
    return fetch(SERVICE_URLS[serviceKey as keyof typeof SERVICE_URLS], options);
  }
  throw new Error(`Unknown server requested!: ${body.operationName}`);
};
const link = createHttpLink({ fetch: customFetch, credentials: 'include' });

const additiveLink = ApolloLink.from([errorLink, link]);

const apolloClient = new ApolloClient({
  cache: new InMemoryCache({
    addTypename: false,
  }),
});
apolloClient.setLink(additiveLink);

export default apolloClient;
